import { useEffect, useContext, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import cn from 'clsx'
import _noop from 'lodash/noop'
import _isEmpty from 'lodash/isEmpty'

import Page from '../../components/Page/Page'
import PageLoader from '../../components/PageLoader/PageLoader'
import Input from '../../components/Input/Input'
import useDocumentTitle from '../../hooks/use_document_title'
import useClassName from '../../hooks/use_class_name'
import useHandleSubmit from './hooks/useHandleSubmit'
import { AuthStates, DOCUMENT_TITLE, PATH, CLASS_NAME } from './const'
import AuthContext from 'auth_context'
import { PATH as DASHBOARD_PAGE_PATH } from 'pages/dashboard/Dashboard'

import './style.scss'

type Props = {
  className?: string
}

export default function Login({ className }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, setUser } = useContext(AuthContext)

  useEffect((): void => {
    if (user !== null) {
      navigate(DASHBOARD_PAGE_PATH)
    }
  }, [user])

  useDocumentTitle(DOCUMENT_TITLE)

  const [loading, setLoading] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const [cognitoUserState, setCognitoUserState] = useState()
  const finalClassName = useClassName(CLASS_NAME, className)
  const [authState, setAuthState] = useState(AuthStates.SignIn)

  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [resetCode, setResetCode] = useState<string>('')

  const isEmailDisabled =
    authState === AuthStates.NewPassword ||
    authState === AuthStates.ExpiredCodeException ||
    loading

  const isPasswordDisabled =
    authState === AuthStates.ResetPassword ||
    authState === AuthStates.NewPassword ||
    authState === AuthStates.ExpiredCodeException ||
    loading

  const handleInputChange = useCallback(
    (setter: (value: string) => void, value: string) => {
      setter(value)
    },
    []
  )

  const handleResetPassword = useCallback(
    (): void => {
      // https://app.asana.com/1/1205465676647380/project/1207268734114690/task/1209913679025622
      // before the aforementioned task is done, as a temporary fix we need to open the support form
      // https://app.asana.com/1/1205465676647380/project/1206979674459877/task/1210005567325785
      const supportFormUrl = 'https://carbonspace.tech/feedbackform'
      window.open(supportFormUrl, '_blank')

      // setAuthState(AuthStates.ResetPassword)
      setPassword('')
      setEmail('')
      setFormError('')
    },
    [
      // setAuthState
    ]
  )

  const handleSignIn = useCallback((): void => {
    setAuthState(AuthStates.SignIn)
    setPassword('')
    setEmail('')
    setFormError('')
  }, [setAuthState])

  const handleSubmit = useHandleSubmit({
    email,
    password,
    newPassword,
    resetCode,
    authState,
    cognitoUserState,
    setFormError,
    setLoading,
    setAuthState,
    setCognitoUserState,
    setUser
  })

  if (loading) return <PageLoader description={t('loading_message')} />

  // prettier-ignore
  return (
    <Page className={finalClassName} title={DOCUMENT_TITLE}>
      <img className="logo" src="/img/logo.png" alt="CarbonSpace logo"></img>
      <form
        className="cst-page-login-form-wrapper"
        onSubmit={
          loading
            ? _noop
            : authState === AuthStates.SignIn
              ? handleResetPassword
              : handleSignIn
        }
      >
        <h1>{(
          (authState === AuthStates.ResetPassword) ||
          (authState === AuthStates.ResetPasswordStep2)
        )
          ? t('login.title_reset_password')
          : t('login.title_login')}
        </h1>
        <Input
          type="text"
          id="cst-login-email"
          label={t('login.email_label')}
          placeholder={t('login.email_placeholder')}
          value={email}
          disabled={isEmailDisabled}
          className={cn('primary', { disabled: isEmailDisabled })}
          onChange={
            isEmailDisabled ? _noop : handleInputChange.bind(null, setEmail)
          }
        />
        {(
          (authState === AuthStates.SignIn) ||
          (authState === AuthStates.ResetPasswordStep2)
        ) && (
          <Input
            type="password"
            id="cst-login-password"
            label={t('login.password_label')}
            placeholder={
              authState === AuthStates.ResetPasswordStep2
                ? t('login.new_password_placeholder')
                : t('login.password_placeholder')}
            disabled={isPasswordDisabled}
            value={password}
            className={cn('primary', {
              disabled: isPasswordDisabled
            })}
            onChange={
              isPasswordDisabled ? _noop : handleInputChange.bind(null, setPassword)
            }
          />
        )}
        {authState === AuthStates.ResetPasswordStep2 && (
          <Input
            type="text"
            id="cst-login-reset-code"
            placeholder={t('login.reset_code_placeholder')}
            className={cn('primary', { disabled: loading })}
            value={resetCode}
            disabled={loading}
            onChange={loading ? _noop : handleInputChange.bind(null, setResetCode)}
          />
        )}
        {authState === AuthStates.NewPassword && (
          <Input
            type="password"
            id="cst-login-new-password"
            placeholder={t('login.new_password_placeholder')}
            className={cn('primary', { disabled: loading })}
            value={newPassword}
            disabled={loading}
            onChange={
              loading ? _noop : handleInputChange.bind(null, setNewPassword)
            }
          />
        )}
        {!_isEmpty(formError) && <p className="cst-login-error">{formError}</p>}
        <br />
        <br />
        <button
          type="submit"
          className={cn('primary', { disabled: loading })}
          disabled={loading}
          onClick={loading ? _noop : handleSubmit}
        >
          {authState === AuthStates.ExpiredCodeException
            ? t('login.resend_code_button')
            : authState === AuthStates.ResetPassword
              ? t('login.reset_password_button')
              : t('login.sign_in_button')}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={authState === AuthStates.SignIn ? handleResetPassword : handleSignIn}
          className={cn('secondary', { disabled: loading })}
        >
          {authState === AuthStates.SignIn
            ? t('login.forgot_button')
            : t('login.sign_in_instead_button')}
        </button>
      </form>
    </Page>
  )
}

export { PATH, CLASS_NAME }
